import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Card,
  CardContent,
  Alert,
  Divider,
  IconButton,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Bolt as BoltIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material'
import { getRoom } from '@/services/roomService'
import { RoomType, ROOM_TYPE_LABELS } from '@/types/profile'
import { RoomSubInfoRegistrationForm } from '@/components/RoomSubInfoRegistrationForm'
import { toRoomType } from '@/utils/roomType'

export const SubInfoEditFormPage = () => {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [roomName, setRoomName] = useState<string>('')
  const [roomType, setRoomType] = useState<RoomType>('business')
  const [roomLoading, setRoomLoading] = useState(true)
  const [roomError, setRoomError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null)

  useEffect(() => {
    const fetchRoom = async () => {
      if (!roomId) {
        setRoomError('ルームIDが指定されていません')
        setRoomLoading(false)
        return
      }

      try {
        setRoomLoading(true)
        setRoomError(null)
        const roomData = await getRoom(roomId)
        setRoomName(roomData.roomName)
        setRoomType(toRoomType(roomData.categoryType))
      } catch (error) {
        console.error('Failed to fetch room:', error)
        setRoomError('ルーム情報の取得に失敗しました')
      } finally {
        setRoomLoading(false)
      }
    }

    fetchRoom()
  }, [roomId])

  if (roomLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (roomError || !roomId) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
        <Alert severity="error">{roomError || 'ルームが見つかりません'}</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="sticky" sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ px: { xs: 1.5, sm: 2 } }}>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            <Box
              sx={{
                width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 },
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <BoltIcon sx={{ color: 'white', fontSize: { xs: 18, sm: 20 } }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                サブ情報編集
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {roomName}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={ROOM_TYPE_LABELS[roomType]}
            size="small"
            sx={{ fontSize: '0.7rem', flexShrink: 0 }}
          />
        </Toolbar>
      </AppBar>

      {/* Main */}
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          ルーム管理者が設定した項目を入力してください。<br />
          任意の入力で構いません。
        </Alert>

        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            {saveSuccess && (
              <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }}>
                {saveSuccess}
              </Alert>
            )}
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700} sx={{ mb: 0.5 }}>
              追加情報入力
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              ルーム設定に応じた項目を表示します
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <RoomSubInfoRegistrationForm
              roomId={roomId}
              onSaved={() => setSaveSuccess('サブ情報を保存しました。')}
              submitLabel="保存する"
              fieldSize={isMobile ? 'small' : 'medium'}
            />
          </CardContent>
        </Card>
      </Container>
    </Box>
  )
}
